import { t } from 'i18next';
import { useState } from 'react';

import { useUserPermissions } from 'auth/hooks';
import { ImportType } from 'bom/enums';
import { ActionButton } from 'core/components';
import { AccessPermissions, ActionButtonType, NotificationPosition } from 'core/enums';
import { useNotificationContext } from 'core/states';
import { useImportVendorQuote } from 'quotes/components/ImportVendorQuote';
import { QuotePartsImportBom } from 'quotes/components/QuotePartsImportBom';
import { QuoteEntityType } from 'quotes/enums';
import { VendorQuoteHelper } from 'quotes/helpers';
import { IQuoteQuoteAssociationForm, IVendorQuote } from 'quotes/types';

import { ImportVendorQuoteAction } from './ImportVendorQuoteAction';

type IProps = {
  quote?: IVendorQuote;
  quoteEntityType?: QuoteEntityType;
  importVendorQuoteProps?: IImportVendorQuoteActionProps;
};

type IImportVendorQuoteActionProps = {
  quote?: IVendorQuote;
  onImportComplete?: (quoteId: number) => void;
  refetchQueriesKeys?: string[];
  withoutConfirmation?: boolean;
  isUpdatingCurrentQuote?: boolean;
  disabled?: boolean;
};

export const ImportButton = ({
  quote,
  quoteEntityType = QuoteEntityType.QUOTE,
  importVendorQuoteProps = {},
}: IProps) => {
  const [quoteQuoteAssociation, setQuoteQuoteAssociation] = useState<IQuoteQuoteAssociationForm>();
  const [isOpenImportBom, setIsOpenImportBom] = useState(false);

  const { isLoadingTopNotificationDisplayed } = useNotificationContext();
  const { hasPermission } = useUserPermissions();

  const {
    importedQuoteData,
    extractedImportId,
    vendorContact,
    vendorMapping,
    vendorQuoteNumber,
    vendorQuoteRevision,
    isFinalizeFailed,
    isOpenImportVendorQuoteForm,
    onCloseImportVendorQuoteForm,
    onImportVendorQuote,
    onUploadVendorQuote,
    confirmationOption,
    resolveVendorQuoteDuplicate,
    startVendorQuoteImport,
  } = useImportVendorQuote({
    currentProjectId: quote?.vendorProjectId,
    currentJobId: quote?.bomId,
    currentQuoteId: quote?.id,
    notificationPosition: NotificationPosition.TOP,
    setQuoteQuoteAssociation,
    ...importVendorQuoteProps,
    onImportComplete: () =>
      importVendorQuoteProps?.onImportComplete &&
      importVendorQuoteProps.onImportComplete(quoteQuoteAssociation?.vendorQuotes?.[0]),
  });

  const isImportedVendorQuote = VendorQuoteHelper.isImportedVendorQuote({ quote, quoteEntityType });

  if (isImportedVendorQuote) {
    // The upload endpoint rejects users without the resource, so offer no import control at all
    // rather than a button that always 403s. The dialog goes with it — it is only reachable
    // through the upload.
    if (!hasPermission(AccessPermissions.IMPORT_VENDOR_QUOTE)) {
      return null;
    }

    return (
      <>
        <ActionButton
          action={{
            type: ActionButtonType.BUTTON_FILE_UPLOAD,
            label: t('buttonLabelImportUpdatedVendorQuote'),
            disabled: isLoadingTopNotificationDisplayed,
            onUpload: (file: File) => onUploadVendorQuote(file, quote?.id),
          }}
        />

        <ImportVendorQuoteAction
          key={isOpenImportVendorQuoteForm.toString()}
          quote={quote}
          importedQuoteData={importedQuoteData}
          extractedImportId={extractedImportId}
          vendorContact={vendorContact}
          vendorMapping={vendorMapping}
          vendorQuoteNumber={vendorQuoteNumber}
          vendorQuoteRevision={vendorQuoteRevision}
          isFinalizeFailed={isFinalizeFailed}
          isOpenImportVendorQuoteForm={isOpenImportVendorQuoteForm}
          onCloseImportVendorQuoteForm={onCloseImportVendorQuoteForm}
          onImportVendorQuote={onImportVendorQuote}
          resolveVendorQuoteDuplicate={resolveVendorQuoteDuplicate}
          startVendorQuoteImport={startVendorQuoteImport}
          confirmationOption={confirmationOption}
        />
      </>
    );
  }

  return (
    <>
      <ActionButton
        action={{
          type: ActionButtonType.BUTTON,
          label: t('buttonLabelImportBom'),
          disabled: isLoadingTopNotificationDisplayed,
          onClick: () => setIsOpenImportBom(true),
        }}
      />

      <QuotePartsImportBom
        isOpen={isOpenImportBom}
        onClose={() => setIsOpenImportBom(false)}
        quote={quote}
        type={ImportType.VENDOR_QUOTE}
      />
    </>
  );
};
